import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';

function HumidityMeter({
  value,
  min,
  max,
  size = 140,
  optimalMin,
  optimalMax,
  color,
}) {
  const normalizedValue = Math.min(Math.max((value - min) / (max - min), 0), 1);
  const percentage = Math.round(normalizedValue * 100);

  const centerX = size / 2;
  const centerY = size / 2;
  const radius = size * 0.38;
  const strokeWidth = Math.max(6, size * 0.085);

  // Scale factor for icon and text (based on default size of 140)
  const scale = size / 140;

  // Calculate stroke dash for circular progress
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - normalizedValue);

  // Determine color based on value, or use custom color
  const getColor = () => {
    if (color) return color;
    if (value < optimalMin) return '#f97316'; // too dry - orange
    if (value > optimalMax) return '#3b82f6'; // too wet - blue
    return '#22c55e'; // optimal - green
  };

  const progressColor = getColor();

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: size,
      height: size,
    }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <linearGradient id="humidityGradient" x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stopColor={progressColor} stopOpacity={0.8} />
            <stop offset="100%" stopColor={progressColor} stopOpacity={0.4} />
          </linearGradient>
          <filter id="humidityGlow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <clipPath id="dropletClip">
            <path d={`
              M ${centerX} ${centerY - radius * 1.1}
              Q ${centerX + radius * 0.8} ${centerY - radius * 0.3} ${centerX + radius * 0.7} ${centerY + radius * 0.3}
              Q ${centerX + radius * 0.6} ${centerY + radius * 0.9} ${centerX} ${centerY + radius}
              Q ${centerX - radius * 0.6} ${centerY + radius * 0.9} ${centerX - radius * 0.7} ${centerY + radius * 0.3}
              Q ${centerX - radius * 0.8} ${centerY - radius * 0.3} ${centerX} ${centerY - radius * 1.1}
              Z
            `} />
          </clipPath>
        </defs>

        {/* Background track */}
        <circle
          cx={centerX}
          cy={centerY}
          r={radius}
          fill="none"
          stroke="#1f2937"
          strokeWidth={strokeWidth}
        />

        {/* Optimal range arc */}
        <circle
          cx={centerX}
          cy={centerY}
          r={radius + strokeWidth / 2 + 4}
          fill="none"
          stroke="#22c55e"
          strokeWidth={3}
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - (optimalMax - optimalMin) / (max - min))}
          strokeLinecap="round"
          opacity={0.3}
          transform={`rotate(${-90 + 360 * (optimalMin - min) / (max - min)} ${centerX} ${centerY})`}
        />

        {/* Progress arc */}
        <circle
          cx={centerX}
          cy={centerY}
          r={radius}
          fill="none"
          stroke={progressColor}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${centerX} ${centerY})`}
          filter="url(#humidityGlow)"
        />

        {/* Water droplet icon in center - scaled */}
        <g transform={`translate(${centerX - 15 * scale}, ${centerY - 25 * scale}) scale(${scale})`}>
          <path
            d="M15 3
               Q22 12 22 18
               Q22 26 15 28
               Q8 26 8 18
               Q8 12 15 3
               Z"
            fill="none"
            stroke={progressColor}
            strokeWidth={2}
            opacity={0.8}
          />
          {/* Fill level inside droplet */}
          <clipPath id="dropFill">
            <path d="M15 3 Q22 12 22 18 Q22 26 15 28 Q8 26 8 18 Q8 12 15 3 Z" />
          </clipPath>
          <rect
            x={8}
            y={28 - 25 * normalizedValue}
            width={14}
            height={25 * normalizedValue}
            fill={progressColor}
            clipPath="url(#dropFill)"
            opacity={0.6}
          />
        </g>

        {/* Percentage text - scaled */}
        <text
          x={centerX}
          y={centerY + radius * 0.6}
          textAnchor="middle"
          fill="#e5e7eb"
          fontSize={Math.max(10, 14 * scale)}
          fontWeight="600"
        >
          {percentage}%
        </text>

        {/* Wave effect at bottom */}
        <ellipse
          cx={centerX}
          cy={centerY + radius * 0.15}
          rx={radius * 0.3}
          ry={radius * 0.08}
          fill={progressColor}
          opacity={0.2}
        >
          <animate
            attributeName="rx"
            values={`${radius * 0.3};${radius * 0.35};${radius * 0.3}`}
            dur="2s"
            repeatCount="indefinite"
          />
        </ellipse>
      </svg>
    </Box>
  );
}

HumidityMeter.propTypes = {
  value: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  size: PropTypes.number,
  optimalMin: PropTypes.number,
  optimalMax: PropTypes.number,
  color: PropTypes.string,
};

export default HumidityMeter;
